/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "../CSS/ProfileDetailsView.css";
import ProfileExpandableSection from "./ProfileExpandableSection";
import underReviewIcon from "../../../assets/images/dashboard-under-review-icon.svg";
import infoIcon from "../../../assets/images/dashboard-info-icon.svg";
import editIcon from "../../../assets/images/profile-edit-icon.svg";
import verifiedIcon from "../../../assets/images/profile-verfified-icon.svg";
import unverifiedIcon from "../../../assets/images/profile-unverified-iocn.svg";
import saveIcon from "../../../assets/images/profile-save-icon.svg";
import ProfileNonExpandableSection from "./ProfileNonExpandableSection";
import { MENU_ITEM, PROFILE_STATUS } from "utils/constants";
import { WEB_LINK } from "utils/strings";
import BecomeMemberDialog from "components/elements/BecomeMemberDialog";
import { useTabBar } from "services/TabBarProvider";
import { useLogout } from "services/LogoutProvider";
import { useStorage } from "services/StorageProvider";
import { useNavigate } from "react-router-dom";
import ROUTE_PATHS from "utils/routePaths";
import { useMutation } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import { CircularProgress } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

interface ProfileDetailsViewProps {
  isActiveMember: boolean;
}

const ProfileDetailsView: FC<ProfileDetailsViewProps> = ({
  isActiveMember,
}) => {
  const navigate = useNavigate();
  const { setSelectedTabBar } = useTabBar();
  const { handleAppExit } = useLogout();
  const { userProfileData, updateUserProfileData } = useStorage();
  const [showBecomeMemberDialog, setShowBecomeMemberDialog] =
    useState<boolean>(false);
  const [phoneEditing, setPhoneEditing] = useState(false);
  const [tempPhoneNumber, setTempPhoneNumber] = useState("");
  const [linkedInEditing, setLinkedInEditing] = useState(false);
  const [tempProfileLink, setTempProfileLink] = useState("");
  const [dobEditing, setDOBEditing] = useState(false);
  const [tempDOB, setTempDOB] = useState("");
  const [genderEditing, setGenderEditing] = useState(false);
  const [tempGender, setTempGender] = useState("");
  const { updateUserProfile } = useWSMApi();

  const updateProfileLinkMutation = useMutation({
    mutationFn: () =>
      updateUserProfile({
        profile_link: tempProfileLink,
      }),
    onSuccess: (response) => {
      updateUserProfileData(response);
      setLinkedInEditing(false);
    },
  });

  const updateDOBMutation = useMutation({
    mutationFn: () =>
      updateUserProfile({
        date_of_birth: tempDOB,
      }),
    onSuccess: (response) => {
      updateUserProfileData(response);
      setDOBEditing(false);
    },
  });

  const updateGenderMutation = useMutation({
    mutationFn: () =>
      updateUserProfile({
        gender: tempGender,
      }),
    onSuccess: (response) => {
      updateUserProfileData(response);
      setGenderEditing(false);
    },
  });

  const updatePhoneMutation = useMutation({
    mutationFn: () =>
      updateUserProfile({
        mobile_number: "+" + tempPhoneNumber,
      }),
    onSuccess: (response) => {
      updateUserProfileData(response);
      setPhoneEditing(false);
    },
  });

  useEffect(() => {
    if (!phoneEditing) {
      setTempPhoneNumber(userProfileData?.mobile_number || "");
    }
    if (!linkedInEditing) {
      setTempProfileLink(userProfileData?.profile_link || "");
    }
    if (!dobEditing) {
      setTempDOB(userProfileData?.date_of_birth || "");
    }
    if (!genderEditing) {
      setTempGender(userProfileData?.gender || "");
    }
  }, [
    userProfileData?.mobile_number,
    userProfileData?.profile_link,
    userProfileData?.date_of_birth,
    userProfileData?.gender,
  ]);

  const handleBecomeMemberClick = () => {
    if (userProfileData?.credit?.status === PROFILE_STATUS.NON_MEMBER) {
      navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
        state: {
          anyData: {
            title: "Become A Member",
            url: WEB_LINK.BECOME_MEMBER,
          },
        },
      });
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.UNDER_REVIEW
    ) {
      setShowBecomeMemberDialog(true);
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.PAYMENT_PENDING
    ) {
      setSelectedTabBar(MENU_ITEM.MEMBERSHIP);
    }
  };

  const handlePhoneCancel = () => {
    setPhoneEditing(false);
    setTempPhoneNumber(userProfileData?.mobile_number || "");
  };

  const handlePhoneSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    updatePhoneMutation.mutate();
  };

  const handleLinkedInCancel = () => {
    setLinkedInEditing(false);
    setTempProfileLink(userProfileData?.profile_link || "");
  };

  const handleLinkedInSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateProfileLinkMutation.mutate();
  };

  const handleDOBCancel = () => {
    setDOBEditing(false);
    setTempDOB(userProfileData?.date_of_birth || "");
  };

  const handleDOBSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateDOBMutation.mutate();
  };

  const handleGenderCancel = () => {
    setGenderEditing(false);
    setTempGender(userProfileData?.gender || "");
  };

  const handleGenderSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateGenderMutation.mutate();
  };

  const getFooterContent = () => {
    if (userProfileData?.credit?.status === PROFILE_STATUS.NON_MEMBER) {
      return (
        <div className="profile-screen-footer-container">
          <button
            className="non-membership-button"
            onClick={handleBecomeMemberClick}
          >
            Become a Member
          </button>
        </div>
      );
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.UNDER_REVIEW
    ) {
      return (
        <div className="profile-screen-footer-container">
          <button
            className="profile-screen-become-member-button"
            onClick={handleBecomeMemberClick}
          >
            Become a Member
            <img src={underReviewIcon} alt="under-review-icon" />
          </button>
          <div className="info-container">
            <img src={infoIcon} alt="info-icon" />
            <span>
              Membership Review is{" "}
              <span style={{ color: "#ff6b35" }}>Under Way.</span>
            </span>
          </div>
        </div>
      );
    } else if (
      userProfileData?.credit?.status === PROFILE_STATUS.PAYMENT_PENDING
    ) {
      return (
        <div className="profile-screen-footer-container">
          <button
            className="profile-screen-become-member-button"
            style={{ backgroundColor: "#C1F700" }}
            onClick={handleBecomeMemberClick}
          >
            You’re in .. Almost
          </button>
          <div className="info-container" style={{ width: "288px" }}>
            <img src={infoIcon} alt="info-icon" />
            <span>
              Membership Approved,{" "}
              <span style={{ color: "#ff6b35" }}>Complete your payment</span>
            </span>
          </div>
        </div>
      );
    }
  };

  return (
    <div style={{ marginBottom: "40px" }}>
      <ProfileExpandableSection title="Personal" subTitle="Details">
        <div className="item-container">
          <span>{userProfileData?.email}</span>
          <img src={userProfileData?.is_verified ? verifiedIcon : unverifiedIcon} alt="verified-icon" />
        </div>
        <div
          className="item-container"
          onClick={() => !phoneEditing && setPhoneEditing(true)}
        >
          <PhoneInput
            country={"in"}
            value={
              phoneEditing
                ? tempPhoneNumber
                : userProfileData?.mobile_number || ""
            }
            onChange={(phone) => phoneEditing && setTempPhoneNumber(phone)}
            countryCodeEditable={false}
            inputProps={{
              name: "mobile",
              required: true,
              autoFocus: phoneEditing,
              autoComplete: "off",
            }}
            containerClass="profile-screen-details-custom-phone"
            inputClass="profile-screen-details-custom-phone-input"
          />
          {phoneEditing ? (
            <>
              <div className="save-button" onClick={handlePhoneSave}>
                {updatePhoneMutation?.isPending ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <>
                    <img src={saveIcon} alt="save-icon" /> Save
                  </>
                )}
              </div>
              <div className="cancel-button" onClick={handlePhoneCancel}>
                ✕
              </div>
            </>
          ) : (
            <img src={editIcon} alt="url-info-icon" />
          )}
        </div>
        <div
          className="item-container"
          onClick={() => !linkedInEditing && setLinkedInEditing(true)}
        >
          <input
            placeholder="Paste Your LinkedIn URL"
            value={
              linkedInEditing
                ? tempProfileLink
                : userProfileData?.profile_link || ""
            }
            onChange={(e) =>
              linkedInEditing && setTempProfileLink(e.target.value)
            }
            readOnly={!linkedInEditing}
            type="url"
            autoComplete="url"
          />
          {linkedInEditing ? (
            <>
              <div className="save-button" onClick={handleLinkedInSave}>
                {updateProfileLinkMutation?.isPending ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <>
                    <img src={saveIcon} alt="save-icon" /> Save
                  </>
                )}
              </div>
              <div className="cancel-button" onClick={handleLinkedInCancel}>
                ✕
              </div>
            </>
          ) : (
            <img src={editIcon} alt="url-info-icon" />
          )}
        </div>
        <div
          className="item-container"
          onClick={() => !dobEditing && setDOBEditing(true)}
        >
          <DatePicker
            enableAccessibleFieldDOMStructure={false}
            disabled={!dobEditing}
            format="DD/MM/YYYY"
            value={
              (dobEditing ? tempDOB : userProfileData?.date_of_birth)
                ? dayjs(dobEditing ? tempDOB : userProfileData?.date_of_birth)
                : null
            }
            onChange={(newValue) => {
              if (dobEditing) {
                setTempDOB(newValue ? newValue.format("YYYY-MM-DD") : "");
              }
            }}
            disableFuture
            slotProps={{
              textField: {
                className: "dob-picker",
                variant: "standard",
                placeholder: "Enter Date of Birth",
                InputProps: {
                  disableUnderline: true,
                },
              },
            }}
          />
          {dobEditing ? (
            <>
              <div className="save-button" onClick={handleDOBSave}>
                {updateDOBMutation?.isPending ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <>
                    <img src={saveIcon} alt="save-icon" /> Save
                  </>
                )}
              </div>
              <div className="cancel-button" onClick={handleDOBCancel}>
                ✕
              </div>
            </>
          ) : (
            <img src={editIcon} alt="url-info-icon" />
          )}
        </div>
        <div
          className="item-container"
          onClick={() => !genderEditing && setGenderEditing(true)}
        >
          <select
            className={`gender-select ${(genderEditing ? tempGender : userProfileData?.gender)
                ? "selected"
                : "placeholder"
              }`}
            value={genderEditing ? tempGender : userProfileData?.gender || ""}
            onChange={(e) => genderEditing && setTempGender(e.target.value)}
            disabled={!genderEditing}
          >
            <option value="" disabled>
              Select Your Gender
            </option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
          </select>
          {genderEditing ? (
            <>
              <div className="save-button" onClick={handleGenderSave}>
                {updateGenderMutation?.isPending ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <>
                    <img src={saveIcon} alt="save-icon" /> Save
                  </>
                )}
              </div>
              <div className="cancel-button" onClick={handleGenderCancel}>
                ✕
              </div>
            </>
          ) : (
            <img src={editIcon} alt="url-info-icon" />
          )}
        </div>
      </ProfileExpandableSection>
      {isActiveMember && (
        <ProfileNonExpandableSection
          title="UR"
          subTitle="Keys"
          onClick={() => navigate(ROUTE_PATHS.WSM_KEYS)}
        />
      )}
      <ProfileNonExpandableSection
        title="TF Chat"
        subTitle="Support"
        onClick={() => handleAppExit(WEB_LINK.WHATSAPP_CHANNEL)}
      />
      <ProfileNonExpandableSection
        title="FAQ"
        subTitle=""
        onClick={() => navigate(ROUTE_PATHS.WSM_FAQ)}
      />
      <ProfileNonExpandableSection
        title="About"
        subTitle="Us"
        onClick={() => {
          navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
            state: {
              anyData: { title: "About Us", url: WEB_LINK.ABOUT_US },
            },
          });
        }}
      />
      <ProfileNonExpandableSection
        title="Settings"
        subTitle=""
        onClick={() => navigate(ROUTE_PATHS.WSM_SETTINGS_SCREEN)}
      />
      {/* <ProfileNonExpandableSection
        title="TF Score"
        subTitle="Card"
        onClick={() => {
          if (userProfileData?.score_cards?.length > 0) {
            navigate(ROUTE_PATHS.WSM_SCORE_CARD_READINESS_SCREEN, {
              state: {
                hasScoreCards: true,
              },
            });
          } else {
            navigate(ROUTE_PATHS.WSM_SCORE_CARD_INITIAL_SCREEN);
          }
        }}
      /> */}
      {userProfileData?.credit?.status !== PROFILE_STATUS.MEMBER &&
        getFooterContent()}
      {/* <div className="profile-screen-footer-container">
        <button onClick={() => setShowLogoutDialog(true)}>Logout</button>
      </div> */}
      <BecomeMemberDialog
        profileStatus={userProfileData?.credit?.status}
        open={showBecomeMemberDialog}
        onClose={() => setShowBecomeMemberDialog(false)}
      />
    </div>
  );
};

export default ProfileDetailsView;
